import { Card, Table, TableProps } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Widget } from './Widget';
import css from './TableWidget.module.less';
import cn from 'classnames';
import { GetRowKey } from 'rc-table/es/interface';
import { RemoteData, success } from '@devexperts/remote-data-ts';
import { renderRemoteData } from '../ui-kit/Loading';
import { isRemoteData } from '../../utils/remote-data.utils';
import { ColumnSelector } from './ColumnSelector';
import { ColumnVisibilityConfig, filterVisibleColumns, useColumnVisibility } from '../../utils/table-columns.utils';
import { useIsMobile } from '../../utils/context.utils';

interface TableWidgetTheme {
	card?: string;
}

/** Key of the synthetic, pinned-right column that hosts the column-selector control. */
const SELECTOR_COL_KEY = '__columnSelector__';

/**
 * Breathing room below a 'fill' table: the app shell's bottom padding plus a
 * little, so the last row never sits flush against the viewport edge.
 */
const FILL_BOTTOM_GAP = 60;

/** Floor for a filled table — a short viewport should still show a few rows. */
const FILL_MIN_HEIGHT = 160;

/** Nearest ancestor that actually scrolls vertically — the app shell content pane. */
function findScrollParent(el: HTMLElement): HTMLElement | null {
	for (let node = el.parentElement; node; node = node.parentElement) {
		const overflowY = window.getComputedStyle(node).overflowY;
		if (overflowY === 'auto' || overflowY === 'scroll') {
			return node;
		}
	}
	return null;
}

/**
 * Measures the viewport space left below the returned ref and reports it as a
 * table-body height, so the body — not the page — owns the vertical scroll
 * (issue #70). Re-measures on window resize and whenever anything above the
 * table reflows.
 *
 * `getBoundingClientRect().top` is viewport-relative and so shrinks as the page
 * scrolls; adding the scrolling ancestor's `scrollTop` back recovers the
 * unscrolled offset, which keeps the result stable if we measure mid-scroll.
 * The rendered header is subtracted because `scroll.y` bounds the *body* alone.
 */
function useFillViewportHeight(enabled: boolean) {
	const ref = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<number>();

	const measure = useCallback(() => {
		const el = ref.current;
		if (!el) {
			return;
		}
		const scroller = findScrollParent(el);
		const top = el.getBoundingClientRect().top + (scroller ? scroller.scrollTop : 0);
		const headerHeight = el.querySelector<HTMLElement>('.ant-table-header')?.offsetHeight ?? 0;
		const next = Math.max(FILL_MIN_HEIGHT, window.innerHeight - top - headerHeight - FILL_BOTTOM_GAP);
		// Ignore sub-pixel churn: the observers below watch elements whose size this
		// height feeds back into, so re-setting an equal value would loop.
		setHeight(prev => (prev !== undefined && Math.abs(prev - next) <= 1 ? prev : next));
	}, []);

	useEffect(() => {
		if (!enabled) {
			setHeight(undefined);
			return;
		}
		measure();
		window.addEventListener('resize', measure);
		const observer = new ResizeObserver(measure);
		const el = ref.current;
		if (el) {
			// The widget itself — catches antd splitting the table into header + body
			// once `scroll.y` lands, which is what makes the header height measurable.
			observer.observe(el);
			// The scroll container's content — catches the chrome above the table
			// reflowing (page title, search box wrapping, sidebar collapse).
			const scrollerContent = findScrollParent(el)?.firstElementChild;
			if (scrollerContent) {
				observer.observe(scrollerContent);
			}
		}
		return () => {
			window.removeEventListener('resize', measure);
			observer.disconnect();
		};
	}, [enabled, measure]);

	return [ref, height] as const;
}

export type TableWidgetProps<T> = Omit<TableProps<T>, 'dataSource'> & {
	rowKey: GetRowKey<T>;
	header?: ReactNode;
	dataSource: RemoteData<unknown, T[]> | T[];
	theme?: TableWidgetTheme;
	/**
	 * Opt-in "Columns" show/hide control. It lives in a narrow, pinned right-hand
	 * header cell (a "settings column"), so it stays inside the table boundaries.
	 * `true` enables it without persistence; pass a config for a localStorage
	 * namespace, default-hidden columns, and always-visible columns.
	 */
	columnSelector?: boolean | ColumnVisibilityConfig;
	/**
	 * Bounds the table body so the *body* owns the vertical scroll instead of the
	 * page. antd then renders the header as a separate, permanently visible
	 * element and parks the horizontal scrollbar at the bottom of the bounded
	 * viewport — no `sticky` needed (issue #70).
	 *
	 * - `'fill'` — fill the viewport height remaining below the table
	 * - a number — fixed body height in px
	 * - omitted — the body grows to fit its rows and the page scrolls
	 *
	 * Inert on mobile, where an inner scroller inside an already-short viewport is
	 * worse than letting the page scroll.
	 */
	bodyHeight?: 'fill' | number;
};

export interface SortOrder<T> {
	sortBy: keyof T;
	order: 'descend' | 'ascend';
}

export function TableWidget<T extends object>({
	columns,
	sticky,
	scroll,
	header,
	dataSource,
	theme,
	columnSelector,
	bodyHeight,
	...props
}: TableWidgetProps<T>) {
	const selectorConfig = useMemo<ColumnVisibilityConfig | null>(
		() => (columnSelector === true ? {} : columnSelector || null),
		[columnSelector],
	);

	// Hook is called unconditionally (rules of hooks); an empty config is inert.
	const visibility = useColumnVisibility(selectorConfig ?? {});

	// Filter to visible columns *before* the title-wrap / className styling below.
	const visibleColumns = useMemo(
		() => (selectorConfig && columns ? filterVisibleColumns(columns, visibility.hidden) : columns),
		[selectorConfig, columns, visibility.hidden],
	);

	const styledColumns = useMemo(
		() =>
			visibleColumns?.map(col => ({
				...col,
				title: function () {
					return <span>{col.title}</span>;
				},
				showSorterTooltip: false,
				className: cn(css.col, col.className),
			})),
		[visibleColumns],
	);

	// Append the column-selector control as a pinned right-hand header cell: its
	// title hosts the dropdown, the body cells are empty. It reads the *raw*
	// `columns` (original string titles), not the styled ones whose titles were
	// replaced with a render function.
	const tableColumns = useMemo(() => {
		const base = styledColumns as ColumnsType<T> | undefined;
		if (!selectorConfig || !base || !columns) {
			return base;
		}
		const selectorCol: ColumnType<T> = {
			key: SELECTOR_COL_KEY,
			fixed: 'right',
			width: 48,
			align: 'center',
			className: css.selectorCol,
			title: () => (
				<ColumnSelector
					columns={columns}
					isHidden={visibility.isHidden}
					alwaysVisible={visibility.alwaysVisible}
					onSetHidden={visibility.setHidden}
				/>
			),
			render: () => null,
		};
		return [...base, selectorCol];
	}, [selectorConfig, styledColumns, columns, visibility]);

	const data = useMemo(
		() => (isRemoteData(dataSource) ? (dataSource as RemoteData<Error, T[]>) : success(dataSource)),
		[dataSource],
	);

	// Mobile renders in a short viewport, where an inner scroller is worse than
	// letting the page scroll — so `bodyHeight` is deliberately inert there.
	const isMobile = useIsMobile();
	const bounded = !isMobile && bodyHeight !== undefined;
	const [fillRef, fillHeight] = useFillViewportHeight(bounded && bodyHeight === 'fill');

	// Undefined on the first paint of a 'fill' table: the height is only knowable
	// once there is a laid-out body to measure. It renders unbounded for that one
	// frame, then settles.
	const bodyScrollY = bounded ? (bodyHeight === 'fill' ? fillHeight : bodyHeight) : undefined;

	const mergedScroll = bodyScrollY === undefined ? scroll : { ...scroll, y: bodyScrollY };

	// A bounded body already gives antd its own pinned header; `sticky` would layer
	// a second, competing mechanism on top of it, and brings back rc-table's
	// synthetic horizontal scroll rail — the duplicate bar from #63.
	const mergedSticky = bounded ? undefined : sticky;

	const content = renderRemoteData(data, {
		success: rows => (
			<Table<T>
				pagination={false}
				{...props}
				scroll={mergedScroll}
				dataSource={rows}
				columns={tableColumns}
				sticky={mergedSticky}
				className={cn(css.table, selectorConfig && css.hasSelector, props.className)}
			/>
		),
	});

	return (
		<Widget className={cn(mergedSticky && css.sticky)} header={header}>
			<Card
				bordered={false}
				className={cn(css.card, mergedSticky && css.sticky, bounded && css.cardBounded, theme?.card)}
			>
				{/*
				 * The measurement anchor for 'fill'. It wraps the whole remote-data render,
				 * not just the resolved table, because it has to be in the DOM on the first
				 * paint: on a cold load the query is still pending when the effect runs, and
				 * an anchor that only appears with the rows leaves the ref null, no observer
				 * attached, and the table unbounded for the life of the page.
				 *
				 * Still only rendered when bounded, so unbounded tables keep their exact
				 * current DOM and the `height: 100%` chain the stylesheet relies on.
				 */}
				{bounded ? (
					<div ref={fillRef} className={css.boundedWrap}>
						{content}
					</div>
				) : (
					content
				)}
			</Card>
		</Widget>
	);
}
